/* eslint-disable max-lines */
import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors as OriginalUnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { FORM_STATES, useFormService } from '@bingads-webui-react/form';
import { ValidationGroup } from '@bingads-webui-react/validation';
import {
  Collapse,
  ValidatedPanel,
  useCollapseState,
  collapseExpander,
  TextTitle,
  ValidatedFixedPanel,
  getPanelErrorProps,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { AssetScenario } from '@bingads-webui-react/copilot-event-provider';
import {
  RecommendationContext,
  withRecommendationContext,
  getODataConfig,
} from '@bingads-webui-campaign-react/perf-max-recommendation';
import {
  useAssetLibraryEventHandler,
  AssetPropertyName,
  logCopilotRecommendations,
  AssetEventFeature,
} from '@bingads-webui-campaign-react/copilot-common';
import { useCopilot } from '@bingads-webui-campaign-react/copilot-experience';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@bingads-webui-react/with-responsive-size';

import { withActivity } from '../with-instrumentation';
import { useAdAssetsInstrument } from '../use-ad-assets-instrument';
import { withCopilotProvider } from '../with-copilot-provider';
import { AdAssetsStepSection, AllAdAssetsStepSections } from '../const';
import { FinalUrl } from './final-url';
import {
  HeadlineAssets,
  LongHeadlineAssets,
  DescriptionAssets,
  getHeadlinesSummaryDetails,
  getLongHeadlinesSummaryDetails,
  getDescriptionsSummaryDetails,
} from './text-assets';
import { BusinessName } from './business-name';
import { ImageList } from './image-list';
import { ImageExperience } from './image-experience';
import { PmaxImageSuggestionCarousel } from './image-suggestion-carousel';
import { ImageListSummaryDetails } from './image-list-details';
import { getAdAssetsFormStyles } from './ad-assets-form-styles';

import FinalUrlIcon from '../media/url-icon.svg';
import ImageIcon from '../media/image-icon.svg';
import TextIcon from '../media/text-icon.svg';
import BusinessIcon from '../media/business-icon.svg';

const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

const CopilotToastOffset = {
  horizontal: 260,
  vertical: 80,
};

const PmaxCopilotMaxImageCount = 20;

const useAdAssetsCollapseState = () => {
  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(AllAdAssetsStepSections, AllAdAssetsStepSections);

  const expandSections = useMemo(() =>
    collapseExpander({ setActiveKeys: setExpandedSections }), [setExpandedSections]);

  return {
    allSections,
    expandedSections,
    setExpandedSections,
    expandSections,
  };
};

export const AdAssetsFormInternal = (props) => {
  const {
    isSaving,
    saveFailed,
    data,
    error,
    onDataChange,
    imageListState,
    onImageListUpdated,
    onImageSelected,
    onClearList,
    onSetCropImageObj,
    onSetEditingPlacementType,
    bypassSettingGlobalLegacyI18n,
    previewTextAssets,
    setPreviewTextAssets,
  } = props;
  const classes = useDefaultStyles(getAdAssetsFormStyles);
  const serverContext = useContext(ServerContext);
  const { scope } = useContext(ScopeContext);
  const newI18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);
  const preferenceService = useContext(PreferenceContext);
  const instrumentation = useContext(InstrumentationContext);
  const adAssetsActivity = useAdAssetsInstrument();
  const { state: formState } = useFormService();

  const i18n = useMemo(() => mergeI18n(newI18n, legacyI18n), [newI18n, legacyI18n]);
  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const {
    cid, aid, campaignId, assetGroupId,
  } = scope.values;

  const {
    assetGroupData,
    shouldPrefill,
  } = data || {};

  const {
    serverErrors = [],
  } = error || {};

  const isEditing = !!assetGroupId;

  const showAllValidationErrors = !_.isEmpty(serverErrors) || saveFailed;
  const areBeaconsActive = showAllValidationErrors && !isSaving;

  const maxImageUploadCountLeft = PmaxCopilotMaxImageCount - _.size(imageListState.imageList);

  const [addNewImagesCallback, setAddNewImagesCallback] = useState(_.noop);
  const [addNewShortHeadlinesCallback, setAddNewShortHeadlinesCallback] = useState(_.noop);
  const [addNewLongHeadlinesCallback, setAddNewLongHeadlinesCallback] = useState(_.noop);
  const [addNewDescriptionCallback, setAddNewDescriptionCallback] = useState(_.noop);

  const {
    Headlines: previewHeadlineKey,
    LongHeadlines: previewLongHeadlineKey,
    Descriptions: previewDescriptionKey,
  } = previewTextAssets;

  useEffect(() => {
    adAssetsActivity.trace(`[AdAssetStep]: cid: ${cid}, aid: ${aid}, campaignId: ${campaignId}`);
  }, [adAssetsActivity, aid, campaignId, cid]);

  const [recommendationContext] = useContext(RecommendationContext);
  const {
    copilotTracking,
    postAssetEventLog = _.noop,
  } = recommendationContext;
  const websiteImageSuggestions = useMemo(() => _.get(recommendationContext, ['images', 'websiteImagesState', 'suggestion']), [recommendationContext]);

  const assetLibraryEventHandler = useAssetLibraryEventHandler(postAssetEventLog);

  useEffect(() => {
    if (formState.matches(FORM_STATES.SAVING_SUCCEEDED)) {
      logCopilotRecommendations({
        recommendationContext,
        assetGroup: assetGroupData,
        isEditing,
      });
    }
  }, [formState, recommendationContext, assetGroupData, isEditing]);

  const {
    allSections,
    expandedSections,
    setExpandedSections,
    expandSections,
  } = useAdAssetsCollapseState();

  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const isBannerNarrow = displayMode !== 'xxl' && displayMode !== 'l';

  const imagesContainerRef = useRef();
  const headlinesSummaryDetails = getHeadlinesSummaryDetails(i18n, assetGroupData);
  const longHeadlinesSummaryDetails = getLongHeadlinesSummaryDetails(i18n, assetGroupData);
  const descriptionsSummaryDetails = getDescriptionsSummaryDetails(i18n, assetGroupData);

  const onAssetGroupDataValidation = useCallback(({ data: newAssetGroupData, messages }) => {
    // When applying a new theme, the validation is returning an array with a null value.
    // That is still a valid value, so we need to compact the array before checking if it is empty.
    const isValid = _.chain(messages)
      .compact()
      .isEmpty()
      .value();

    onDataChange({
      isValid,
      data: prevData => ({
        ...prevData,
        assetGroupData: {
          ...prevData.assetGroupData,
          ...newAssetGroupData,
        },
      }),
    });
  }, [onDataChange]);

  const onImageRemoved = useCallback((imageId) => {
    copilotTracking.removeInitialSuggestion(AssetPropertyName.IMAGE, imageId);
  }, [copilotTracking]);

  const onFinalUrlUpdated = useCallback((newFinalUrl) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroupData: {
          ...prevData.assetGroupData,
          FinalUrls: [newFinalUrl],
        },
      }),
    });
  }, [onDataChange]);

  const updateFinalUrlForRecommendation = useCallback(() => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroupData: {
          ...prevData.assetGroupData,
          FinalUrlForRecommendation: prevData.assetGroupData.FinalUrls ? prevData.assetGroupData.FinalUrls[0] : '',
        },
      }),
    });
  }, [onDataChange]);

  const onReset = useCallback(() => {
    onClearList();
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroupData: {
          ...prevData.assetGroupData,
          Images: {
            ImageList: [],
            PendingImages: [],
            SuggestedImages: [],
          },
          Headlines: {
            Headline0: '',
            Headline1: '',
            Headline2: '',
          },
          LongHeadlines: {
            LongHeadline0: '',
          },
          Descriptions: {
            Description0: '',
            Description1: '',
          },
        },
      }),
    });
  }, [onClearList, onDataChange]);

  const {
    copilotBanner,
    suggestionPoller,
  } = useCopilot({
    maxUploadNum: maxImageUploadCountLeft,
    addNewShortHeadlinesCallback,
    addNewLongHeadlinesCallback,
    addNewDescriptionCallback,
    imageExperienceAddNewImageCallback: addNewImagesCallback,
    onReset,
    cid,
    aid,
    serverContext,
    instrumentation,
    data: shouldPrefill ? _.omit(assetGroupData, 'Id') : assetGroupData,
    enableCopilotRecommendations: true,
    feature: AssetEventFeature.PMAX_LITE,
    assetScenario: AssetScenario.CREATE_WORK_FLOW,
    isBannerNarrow,
    toastOffset: CopilotToastOffset,
  });

  const handleFinalUrlKeyUpEvent = useCallback((e) => {
    const { key } = e;
    if (key === 'Enter') {
      updateFinalUrlForRecommendation();
    }
  }, [updateFinalUrlForRecommendation]);

  const finalUrlAdditionalProps = useMemo(() => ({
    onBlur: updateFinalUrlForRecommendation,
    onKeyUp: handleFinalUrlKeyUpEvent,
  }), [updateFinalUrlForRecommendation, handleFinalUrlKeyUpEvent]);

  const imageListSummaryDetails = useMemo(() => (
    <ImageListSummaryDetails images={imageListState.imageList} />
  ), [imageListState.imageList]);

  const onSetHeadlinePreview = useCallback((key) => {
    setPreviewTextAssets(prev => ({ ...prev, Headlines: key }));
  }, [setPreviewTextAssets]);

  const onSetLongHeadlinePreview = useCallback((key) => {
    setPreviewTextAssets(prev => ({ ...prev, LongHeadlines: key }));
  }, [setPreviewTextAssets]);

  const onSetDescriptionPreview = useCallback((key) => {
    setPreviewTextAssets(prev => ({ ...prev, Descriptions: key }));
  }, [setPreviewTextAssets]);

  if (!aid || !cid) {
    return null;
  }

  return (
    <div className={classNames('ad-assets-form', classes.adAssetsForm)}>
      <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <ValidationGroup
              validationProps={{
                i18n,
                schema: AssetGroup,
                data: assetGroupData,
                onValidate: onAssetGroupDataValidation,
              }}
            >
              <UnmappedFormErrors className="unmapped-server-errors" />
              <Collapse
                skipItemsTypeCheck
                allPanelItems={allSections}
                activeKeys={expandedSections}
                setActiveKeys={setExpandedSections}
              >
                <ValidatedFixedPanel
                  key={AdAssetsStepSection.FinalUrl}
                  title={<TextTitle icon={FinalUrlIcon} titleKey={_TL_('Final URL')} topicKey="pop_BA_NewAdCreate_FinalURL" />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                >
                  <FinalUrl
                    hideLabel
                    additionalProperties={finalUrlAdditionalProps}
                  />
                </ValidatedFixedPanel>
                {copilotBanner}
                <ValidatedPanel
                  key={AdAssetsStepSection.Image}
                  title={<TextTitle icon={ImageIcon} titleKey={_TL_('Images')} topicKey="pop_BA_PMAXLite_Images" />}
                  details={imageListSummaryDetails}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: AdAssetsStepSection.Image,
                    expand: expandSections,
                  }}
                >
                  <ImageList
                    i18n={i18n}
                    imageListState={imageListState}
                    imagesContainerRef={imagesContainerRef}
                  />
                  <PmaxImageSuggestionCarousel
                    i18n={i18n}
                    addImageCallback={addNewImagesCallback}
                    maxUploadNum={maxImageUploadCountLeft}
                  />
                </ValidatedPanel>
                <ValidatedPanel
                  key={AdAssetsStepSection.ShortHeadline}
                  title={<TextTitle icon={TextIcon} titleKey={_TL_('Short headlines')} topicKey="pop_BA_CampaignCreate_AudAds_ShortHeadline" />}
                  details={headlinesSummaryDetails}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: AdAssetsStepSection.ShortHeadline,
                    expand: expandSections,
                  }}
                >
                  <HeadlineAssets
                    setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback}
                    previewHeadlineKey={previewHeadlineKey}
                    onSetHeadlinePreview={onSetHeadlinePreview}
                  />
                </ValidatedPanel>
                <ValidatedPanel
                  key={AdAssetsStepSection.LongHeadline}
                  title={<TextTitle icon={TextIcon} titleKey={_TL_('Long headlines')} topicKey="pop_BA_CampaignCreate_AudAds_LongHeadline" />}
                  details={longHeadlinesSummaryDetails}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: AdAssetsStepSection.LongHeadline,
                    expand: expandSections,
                  }}
                >
                  <LongHeadlineAssets
                    setAddNewLongHeadlinesCallback={setAddNewLongHeadlinesCallback}
                    previewLongHeadlineKey={previewLongHeadlineKey}
                    onSetLongHeadlinePreview={onSetLongHeadlinePreview}
                  />
                </ValidatedPanel>
                <ValidatedPanel
                  key={AdAssetsStepSection.Description}
                  title={<TextTitle icon={TextIcon} titleKey={_TL_('Descriptions')} topicKey="pop_BA_PMAX_Description" />}
                  details={descriptionsSummaryDetails}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expandKey: AdAssetsStepSection.Description,
                    expand: expandSections,
                  }}
                >
                  <DescriptionAssets
                    setAddNewDescriptionCallback={setAddNewDescriptionCallback}
                    previewDescriptionKey={previewDescriptionKey}
                    onSetDescriptionPreview={onSetDescriptionPreview}
                  />
                </ValidatedPanel>
                <ValidatedFixedPanel
                  key={AdAssetsStepSection.BusinessName}
                  title={<TextTitle icon={BusinessIcon} titleKey={_TL_('Business name')} topicKey="pop_BA_CampaignCreate_AudAds_BusinessName" />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                >
                  <BusinessName />
                </ValidatedFixedPanel>
              </Collapse>
              <ImageExperience
                i18n={i18n}
                preferenceService={preferenceService}
                oDataConfig={oDataConfig}
                suggestion={websiteImageSuggestions}
                suggestionPoller={suggestionPoller}
                aid={aid}
                cid={cid}
                campaignId={campaignId}
                assetGroupData={assetGroupData}
                previewTextAssets={previewTextAssets}
                imagesContainerRef={imagesContainerRef}
                imageListState={imageListState}
                setAddNewImagesCallback={setAddNewImagesCallback}
                onImageListUpdated={onImageListUpdated}
                onImageSelected={onImageSelected}
                onImageRemoved={onImageRemoved}
                onSetCropImageObj={onSetCropImageObj}
                onSetEditingPlacementType={onSetEditingPlacementType}
                onFinalUrlUpdated={onFinalUrlUpdated}
                bypassSettingGlobalLegacyI18n={bypassSettingGlobalLegacyI18n}
                assetLibraryEventHandler={assetLibraryEventHandler}
              />
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

AdAssetsFormInternal.propTypes = {
  isSaving: PropTypes.bool.isRequired,
  saveFailed: PropTypes.bool,
  onDataChange: PropTypes.func.isRequired,
  data: PropTypes.shape({
    assetGroupData: PropTypes.shape({}),
  }).isRequired,
  error: PropTypes.shape({}),
  imageListState: PropTypes.shape({
    imageList: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onImageListUpdated: PropTypes.func.isRequired,
  onImageSelected: PropTypes.func.isRequired,
  onClearList: PropTypes.func.isRequired,
  onSetCropImageObj: PropTypes.func.isRequired,
  onSetEditingPlacementType: PropTypes.func.isRequired,
  bypassSettingGlobalLegacyI18n: PropTypes.bool.isRequired,
  previewTextAssets: PropTypes.shape({
    Headlines: PropTypes.string,
    LongHeadlines: PropTypes.string,
    Descriptions: PropTypes.string,
  }).isRequired,
  setPreviewTextAssets: PropTypes.func.isRequired,
};

AdAssetsFormInternal.defaultProps = {
  error: undefined,
  saveFailed: undefined,
};

export const AdAssetsForm = withActivity(withCopilotProvider(withRecommendationContext(AdAssetsFormInternal)));
